import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { importarCheques } from "./ApiServices";
import { formatCuit, formatMoneda } from "../pago_proveedores/utils";
import {
  colorTipoOmision,
  filaOmitido,
  libradorOmitido,
  listaOmitidos,
  motivoOmitido,
  omitidoTieneCodigo,
  refOmitido,
  resumenOmitidos,
} from "./importarOmitidos";

const ACCEPT =
  ".csv,.xlsx,.xls,.txt,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const EXTENSIONES_OK = [".csv", ".xlsx", ".xls", ".txt"];
const PAGE_SIZE = 10;

const esArchivoPermitido = (file) => {
  if (!file?.name) return false;
  const nombre = file.name.toLowerCase();
  return EXTENSIONES_OK.some((ext) => nombre.endsWith(ext));
};

const paginar = (lista, page, pageSize) => {
  const inicio = page * pageSize;
  return lista.slice(inicio, inicio + pageSize);
};

export default function ModalImportarCheques({ open, onClose, onImportacionOk }) {
  const inputRef = useRef(null);
  const [archivo, setArchivo] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [modo, setModo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");
  const [pageValidos, setPageValidos] = useState(0);
  const [pageOmitidos, setPageOmitidos] = useState(0);
  const [filtroOmitido, setFiltroOmitido] = useState("");

  const resetEstado = useCallback(() => {
    setArchivo(null);
    setResultado(null);
    setModo(null);
    setError("");
    setDragOver(false);
    setPageValidos(0);
    setPageOmitidos(0);
    setFiltroOmitido("");
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  useEffect(() => {
    if (!open) resetEstado();
  }, [open, resetEstado]);

  const asignarArchivo = useCallback((file) => {
    if (!file) return;
    if (!esArchivoPermitido(file)) {
      setError("Formato no permitido. Use .csv, .xlsx, .xls o .txt");
      return;
    }
    setArchivo(file);
    setResultado(null);
    setModo(null);
    setError("");
    setPageValidos(0);
    setPageOmitidos(0);
    setFiltroOmitido("");
  }, []);

  const validos = useMemo(() => {
    const candidatos = [
      resultado?.muestra,
      resultado?.validos,
      resultado?.filas_validas,
      resultado?.cheques_validos,
      resultado?.cheques,
    ].filter((list) => Array.isArray(list));
    if (!candidatos.length) return [];
    return candidatos.reduce((mejor, actual) =>
      actual.length > mejor.length ? actual : mejor
    );
  }, [resultado]);

  const omitidos = useMemo(() => listaOmitidos(resultado), [resultado]);

  const resumen = useMemo(() => resumenOmitidos(resultado), [resultado]);

  const omitidosFiltrados = useMemo(
    () =>
      filtroOmitido
        ? omitidos.filter((row) => omitidoTieneCodigo(row, filtroOmitido))
        : omitidos,
    [omitidos, filtroOmitido]
  );

  const validosPagina = useMemo(
    () => paginar(validos, pageValidos, PAGE_SIZE),
    [validos, pageValidos]
  );

  const omitidosPagina = useMemo(
    () => paginar(omitidosFiltrados, pageOmitidos, PAGE_SIZE),
    [omitidosFiltrados, pageOmitidos]
  );

  const handleArchivo = (e) => {
    asignarArchivo(e.target.files?.[0] ?? null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!procesando) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (procesando) return;
    const file = e.dataTransfer?.files?.[0] ?? null;
    asignarArchivo(file);
  };

  const ejecutarUpload = async (confirmar) => {
    if (!archivo) {
      setError("Seleccione o arrastre un archivo CSV o Excel");
      return;
    }

    setProcesando(true);
    setError("");
    setResultado(null);
    setModo(null);
    setFiltroOmitido("");
    setPageValidos(0);
    setPageOmitidos(0);

    try {
      const data = await importarCheques(archivo, confirmar);
      setResultado(data);
      setModo(confirmar ? "aplicado" : "preview");

      if (confirmar) {
        onImportacionOk?.(data);
      }
    } catch (err) {
      setError(err.message || "Error al procesar el archivo");
    } finally {
      setProcesando(false);
    }
  };

  const totalValidos = Number(resultado?.total_validos ?? validos.length ?? 0);
  const totalOmitidos = Number(
    resultado?.total_omitidos ?? omitidos.length ?? 0
  );

  const puedeConfirmar =
    Boolean(archivo) &&
    modo === "preview" &&
    !procesando &&
    totalValidos > 0;

  return (
    <Dialog open={open} onClose={procesando ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Importar cheques</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Subí un archivo Excel o CSV con el listado de cheques recibidos.
          Primero analizá el archivo y después confirmá. Los cheques nuevos
          quedan pendientes de revisión de Cuentas Corrientes. La conciliación
          con el banco (pasar a cartera) se hace en “Conciliar aceptados”.
        </Alert>

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !procesando && inputRef.current?.click()}
          sx={{
            mb: 2,
            py: 1.25,
            px: 2,
            border: "2px dashed",
            borderColor: dragOver ? "#1F618D" : archivo ? "#27AE60" : "#BDC3C7",
            borderRadius: 1.5,
            bgcolor: dragOver ? "rgba(31, 97, 141, 0.08)" : "#FAFBFC",
            cursor: procesando ? "default" : "pointer",
            transition: "border-color 0.15s, background-color 0.15s",
            "&:hover": procesando
              ? undefined
              : { borderColor: "#1F618D", bgcolor: "rgba(31, 97, 141, 0.04)" },
          }}
        >
          <input
            ref={inputRef}
            type="file"
            hidden
            accept={ACCEPT}
            onChange={handleArchivo}
            disabled={procesando}
          />
          {archivo ? (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <InsertDriveFileIcon sx={{ fontSize: 28, color: "#27AE60" }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} color="#1F618D" noWrap title={archivo.name}>
                  {archivo.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(archivo.size / 1024).toFixed(1)} KB — arrastrá otro o clic para cambiar
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CloudUploadIcon sx={{ fontSize: 28, color: "#1F618D" }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="#1F618D">
                  Arrastrá el Excel aquí o hacé clic
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  .csv, .xlsx, .xls, .txt
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {modo === "aplicado" ? (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
            Importación confirmada.
            {resultado?.insertados != null
              ? ` Insertados: ${resultado.insertados}.`
              : ""}
            {resultado?.actualizados != null
              ? ` Actualizados: ${resultado.actualizados}.`
              : ""}
            {resultado?.aviso_cc?.mensaje
              ? ` ${resultado.aviso_cc.mensaje}`
              : resultado?.aviso_cc?.pendientes_cc != null
                ? ` ${resultado.aviso_cc.pendientes_cc} cheque(s) listos para revisión en Cuentas Corrientes.`
                : ""}
          </Alert>
        ) : null}

        {resultado ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {resultado.header_linea != null ? (
              <Chip
                label={`Encabezado línea: ${resultado.header_linea}`}
                size="small"
                variant="outlined"
              />
            ) : null}
            <Chip
              label={`Válidos: ${totalValidos}`}
              size="small"
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Omitidos: ${totalOmitidos}`}
              size="small"
              color={totalOmitidos > 0 ? "warning" : "default"}
              variant="outlined"
            />
            {modo === "aplicado" && resultado.insertados != null ? (
              <Chip
                label={`Insertados: ${resultado.insertados}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            ) : null}
            {modo === "aplicado" && resultado.actualizados != null ? (
              <Chip
                label={`Actualizados: ${resultado.actualizados}`}
                size="small"
                color="info"
                variant="outlined"
              />
            ) : null}
          </Stack>
        ) : null}

        {validos.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {modo === "aplicado"
                ? "Cheques procesados"
                : "OK para importar"}
            </Typography>
            <TableContainer
              sx={{ maxHeight: 280, border: "1px solid #e0e0e0", borderRadius: 1 }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["N° cheque", "ID cheque", "Banco", "Importe", "Fecha pago", "Estado"].map(
                      (h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                          {h}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validosPagina.map((row, index) => (
                    <TableRow
                      key={
                        row.id_cheque ??
                        `${row.nro_cheque}-${pageValidos * PAGE_SIZE + index}`
                      }
                    >
                      <TableCell>{row.nro_cheque ?? "—"}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={row.id_cheque}>
                          {row.id_cheque ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          title={row.banco_emisor}
                          sx={{ maxWidth: 180 }}
                        >
                          {row.banco_emisor ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.importe != null ? formatMoneda(row.importe) : "—"}
                      </TableCell>
                      <TableCell>{row.fecha_pago ?? "—"}</TableCell>
                      <TableCell>{row.estado ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={validos.length}
              page={pageValidos}
              onPageChange={(_e, newPage) => setPageValidos(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </Box>
        ) : modo === "preview" && totalValidos === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No hay filas válidas para importar.
          </Typography>
        ) : null}

        {omitidos.length > 0 ? (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              No se pueden importar
              {totalOmitidos > omitidos.length
                ? ` (mostrando ${omitidos.length} de ${totalOmitidos})`
                : ` (${omitidos.length})`}
            </Typography>
            {resumen.length > 0 ? (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 1.5 }}
              >
                {resumen.map((item) => {
                  const codigo = item.codigo || item.campo || item.mensaje;
                  const activo = filtroOmitido === codigo;
                  return (
                    <Chip
                      key={codigo}
                      size="small"
                      variant={activo ? "filled" : "outlined"}
                      color={colorTipoOmision(item.tipo, item)}
                      label={`${item.mensaje || item.codigo}: ${item.total ?? 0}`}
                      onClick={() => {
                        setFiltroOmitido((prev) =>
                          prev === codigo ? "" : codigo
                        );
                        setPageOmitidos(0);
                      }}
                    />
                  );
                })}
              </Stack>
            ) : null}
            <TableContainer
              sx={{ maxHeight: 320, border: "1px solid #e0e0e0", borderRadius: 1 }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Fila / ref.", "Cheque", "CUIT / librador", "Motivo"].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}
                        >
                          {h}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {omitidosPagina.map((row, index) => {
                    const absIndex = pageOmitidos * PAGE_SIZE + index;
                    const motivo = motivoOmitido(row);
                    const { etiqueta, cuit } = libradorOmitido(row);
                    const ref = refOmitido(row);
                    return (
                      <TableRow
                        key={`${filaOmitido(row, absIndex)}-${row.id_cheque || absIndex}`}
                      >
                        <TableCell>
                          <Typography variant="body2">
                            Fila {filaOmitido(row, absIndex)}
                          </Typography>
                          {ref ? (
                            <Typography variant="caption" color="text.secondary">
                              {ref}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.nro_cheque || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.id_cheque || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 220 }}>
                            {etiqueta}
                          </Typography>
                          {cuit ? (
                            <Typography variant="caption" color="text.secondary">
                              {formatCuit(cuit)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {row.tipo ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={colorTipoOmision(row.tipo, row)}
                                label={row.tipo}
                                sx={{ alignSelf: "flex-start" }}
                              />
                            ) : null}
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{ whiteSpace: "normal" }}
                            >
                              {motivo}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={omitidosFiltrados.length}
              page={pageOmitidos}
              onPageChange={(_e, newPage) => setPageOmitidos(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </Box>
        ) : modo === "preview" && totalOmitidos === 0 && resultado ? (
          <Alert severity="success" sx={{ mb: 1 }}>
            Todas las filas analizadas están listas para importar.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={procesando}>
          Cerrar
        </Button>
        <Button
          variant="outlined"
          startIcon={
            procesando && modo !== "aplicado" ? (
              <CircularProgress size={16} />
            ) : (
              <VisibilityIcon />
            )
          }
          disabled={!archivo || procesando}
          onClick={() => ejecutarUpload(false)}
        >
          Analizar
        </Button>
        <Button
          variant="contained"
          startIcon={
            procesando && modo === "aplicado" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CheckCircleOutlineIcon />
            )
          }
          disabled={!puedeConfirmar}
          onClick={() => ejecutarUpload(true)}
        >
          Confirmar importación
        </Button>
      </DialogActions>
    </Dialog>
  );
}
